/**
 * JSON 정의 장비 — vault.catalog.twin3d/v2 (2026-10-08 사용자: "새 장비는 Three.js 코드가 아니라 JSON(또는 GLB + JSON)만 올리면").
 *
 * 원칙: 렌더러는 제조사 · 모델명을 모른다. 생김새 = geometry, 움직임 = motion, 색 = materials, 값 연결 = bindings.
 *   JSON 에 없는 것은 렌더러도 모른다(추측해서 그리지 않는다).
 *
 * 좌표: Y 위(Y_UP), 단위 m(unit "mm" 이면 1/1000). part.position = 그 도형의 가운데.
 * 숫자 칸에는 숫자 대신 식을 쓸 수 있다 — "$params.trays" · "$params.height / 2" (expr.ts, eval 안 씀).
 * v1(spec_id + params, 형상은 트윈 코드)도 계속 읽는다 — geometry 가 없으면 「기존 파라미터 모델」.
 */

import type { PatternDef } from "./pattern";

export const TWIN3D_V1 = "vault.catalog.twin3d/v1";
export const TWIN3D_V2 = "vault.catalog.twin3d/v2";
/** 카탈로그 모델의 twin_spec_id — 「이 모델은 JSON 정의로 그린다」. 트윈에는 cat:<모델 id> 로 등록된다 */
export const JSON_SPEC_ID = "json_model";
/**
 * [N7] 코드판 트윈 종류 id 38종 — metadata.twin.kind 검사(validate)용. 검사기는 레지스트리를 읽지 않는 순수 함수라 여기 적어 둔다
 *   (specs/index 의 등록 목록과 같은지는 __tests__/twinLink-n7 이 대조 — 종류를 더하면 여기도)
 */
export const TWIN_KINDS: readonly string[] = ["asrs", "shuttle", "autostore", "airrob", "vlm", "miniload", "conveyor", "junction", "amr", "forklift", "dock", "sorter3d", "tsort", "wheel_sorter", "vlift", "elevator", "stairs", "mezzanine", "crossing", "stand", "floor_pallet", "palletizer", "depalletizer", "dps", "pick_station", "das", "erector", "sealer", "labeller", "packing", "tote_supply", "rack", "rack_light", "agf", "poppick", "robot_pick", "acr", "hai_ws", "column", "area"];
export const catalogSpecId = (modelId: string) => `cat:${modelId}`;

/** 숫자 또는 식("$params.x * 2") */
export type NumExpr = number | string;
export type Vec3 = [NumExpr, NumExpr, NumExpr];

export type PrimitiveType = "box" | "cylinder" | "plane" | "mesh" | "sphere" | "hemisphere" | "kit";
/** mesh = 꼭짓점 목록(vertices [x,y,z,…] + indices) — 기본 도형으로 안 되는 모양 · 기존 종류에서 뽑은 모양. 앞으로: extrusion · asset_node */
/** sphere = 타원체 [폭, 높이, 깊이] · hemisphere = 위쪽 반구(밑면 가운데가 원점) [폭, 높이(반구 높이), 깊이] */
/** kit = 실물 부품 키트(shared/detail/kits.ts — 바퀴 · 범퍼 · 라이다 · 모터 · 경광등 …) — kit 에 이름, size = 들어갈 상자 [x,y,z] */
export const PRIMITIVES: PrimitiveType[] = ["box", "cylinder", "plane", "mesh", "sphere", "hemisphere", "kit"];

export interface PartDef {
  id: string;
  type: PrimitiveType;
  /**
   * box = [폭 x, 높이 y, 깊이 z] · cylinder = [지름, 높이, (무시)] — 세로(Y) 원통, 눕히려면 rotation
   * plane = [폭 x, (무시), 깊이 z] — 바닥에 누운 면, 세우려면 rotation
   * mesh = 쓰지 않음(vertices 가 모양)
   */
  size?: Vec3;
  /** mesh — 꼭짓점 좌표 [x,y,z, x,y,z, …](part 기준, 단위 = geometry.unit) */
  vertices?: number[];
  /** mesh — 삼각형 꼭짓점 번호 [a,b,c, …] (없으면 vertices 를 3개씩 삼각형) */
  indices?: number[];
  /** 가운데 위치(부모가 있으면 부모 기준) */
  position?: Vec3;
  /** 도(°) — x · y · z 순서 */
  rotation?: Vec3;
  /** materials 의 키(없으면 회색 기본) */
  material?: string;
  /** 다른 part 의 id — 그 part 와 같이 움직인다(위치 · 회전이 부모 기준) */
  parent?: string;
  /** type "kit" — 부품 키트 이름(KIT_NAMES) */
  kit?: string;
  /** type "kit" — 키트별 선택 값(예: { "color": "amber" } · { "nx": 4, "nz": 2 }) — 숫자 · 식 · 문자 */
  kit_options?: Record<string, NumExpr>;
  /**
   * [고정밀] "hi" = 가까이 볼 때만 그린다(설비가 화면 높이의 20% 넘을 때 — 볼트 · 바퀴 · 센서 같은 부품) ·
   * "lo" = 멀리 볼 때만(고정밀이 대신하는 단순 모양). 없으면 늘 그린다. geometry.lod_size 로 기준 크기를 바꾼다
   */
  lod?: "hi" | "lo";
  /**
   * [화물 수량] true = 매 프레임 다시 계산 — position · visible · repeat 의 item_position · item_rotation · visible_if.
   * 식에서 fitem(k) · fprog(k)(처리 중 k 번째 화물 번호 · 진행률 · 없으면 −1) · units(평균, 번호)(화물마다 정수 개수) · seg(진행, 시작, 끝) · $vars.pi(부모 반복 번호) · $vars.i · j · k.
   * 예: 처리 중 화물마다 주문 토트(repeat count = 동시 처리 수 · visible_if "fitem($vars.i) >= 0") · 그 안 낱개(자식 · visible_if "$vars.i < units($params.itemsPerTote, fitem($vars.pi))")
   */
  live?: boolean;
  /** false 또는 식(0 = 숨김) — $flow 를 쓰면 매 프레임 다시 계산(예: 흐름에 묶이면 자체 화물 숨김 "$flow.coupled == 0") */
  visible?: boolean | string;
  /**
   * true = 미리보기 전용 — 카탈로그 미리보기에는 그리고, 트윈 설비 캔버스에서는 숨긴다(그 자리는 다른 렌더러가 그림 — 예: 단독 랙은 레이아웃 탭 CAD 렌더러).
   * 코드판 specs/rack.ts 의 userData.previewOnly 와 같은 표시(자식 part 까지 같이)
   */
  preview_only?: boolean;
  /**
   * 같은 도형을 여러 개 — offset 만큼씩 옮겨 가며(기본 = 위로 size.y 씩).
   * count2 · offset2 (· count3 · offset3) 를 주면 격자(예: Bay × 단 × Slot) — 번호 = i + count×(j + count2×k)
   */
  repeat?: { count: NumExpr; offset?: Vec3; count2?: NumExpr; offset2?: Vec3; count3?: NumExpr; offset3?: Vec3;
    /** 첫 축(count)을 따라 하나씩 커지는 크기 [x,y,z] — 경사 컨베이어 다리 · 계단처럼 점점 길어지는 것. i 번째 = size + size_step × i */
    size_step?: Vec3;
    /** 반복 도형마다 보일지(0 = 숨김) — 식에서 $vars.i · $vars.j · $vars.k = 첫 · 둘째 · 셋째 축 번호(0부터). 예: 적재율만큼만 채우기 */
    visible_if?: NumExpr;
    /** 반복 도형마다 더하는 위치 [x,y,z] · 회전(도) — 식에서 $vars.i · j · k. 예: hash 로 흩어 놓기 · 줄마다 다른 기준점 */
    item_position?: Vec3;
    item_rotation?: Vec3;
    /** 반복 도형마다 크기 배율 [x,y,z] — 식에서 $vars.i · j · k(예: 짐 크기를 칸마다 다르게) */
    item_scale?: Vec3 };
}

/**
 * [STEP 2-A] GLB 자산(읽기 전용) — glTF 표준대로 m · Y 위. 파일은 url(정적 /twin-assets/ .glb — 검사기 assetUrlIssue) 또는
 *   asset_id(카탈로그 자산 표 — STEP 2-B, 실행 때 resolver 가 내려받기 주소를 받는다) 둘 중 하나.
 *   position(m) · rotation(도, part 와 같은 규칙) · scale(축마다, 0 보다 커야) — 없으면 [0,0,0] · [0,0,0] · [1,1,1].
 *   gltf(+bin) 는 아직 받지 않는다(검사기 오류). 노드 이름 연결(asset.nodes.*)은 STEP 2-B.
 */
export interface AssetGeometry {
  format: "glb" | "gltf";
  /** 정적 개발용 — 같은 출처 /twin-assets/*.glb. asset_id 와 둘 중 하나만 */
  url?: string;
  /** [STEP 2-B] 운영용 — crm_catalog_model_assets.id(UUID). 파일 정보 · 저장 위치 · 검사 결과는 그 표가 정본(여기 중복 저장 안 함 · 서명 URL 도 저장 안 함) */
  asset_id?: string;
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: [number, number, number];
  /** [고정밀] "hi" = GLB 를 가까이 볼 때만(멀면 parts — 대신할 parts 에는 lod "lo"). 이때 parts 가 있어야 한다 */
  lod?: "hi";
}

export interface GeometryDef {
  coordinate_system?: "Y_UP";
  unit?: "m" | "mm";
  /** parts(기본 도형 조합) · asset(GLB — parts 를 같이 쓰면 함께 그린다: 로봇 GLB + 안전 구역 면 같은 혼합) */
  type?: "parts" | "asset";
  parts?: PartDef[];
  asset?: AssetGeometry;
  /** 바닥 크기 [가로 x, 세로 z](배치 · 충돌 · 연결에 쓰는 값) — 없으면 도형 범위로 어림(통로 표시 같은 것까지 들어간다) */
  footprint?: [NumExpr, NumExpr];
  /** [고정밀] 고정밀 전환 기준 크기(m) — 이 크기가 화면 높이의 20% 를 넘으면 lod "hi" 를 그린다. 없으면 설비 높이(1.2 ~ 4m) */
  lod_size?: NumExpr;
}

/**
 * 파라미터 → 형상/동작 값. target 꼴:
 *   geometry.parts.<id>.size|position|rotation.x|y|z · geometry.parts.<id>.repeat.count · geometry.parts.<id>.repeat.offset.x|y|z
 *   motion.animations.<id>.speed|distance
 *   asset.nodes.<이름>.scale|position|rotation.x|y|z (GLB — 예정)
 * value = source 값. expression 이 있으면 그 결과("$value + 0.35"), transform "int" 면 반올림.
 */
export interface BindingDef {
  source: string;
  target: string;
  expression?: string;
  transform?: "number" | "int";
}

export type AnimationType = "translate" | "rotate" | "path" | "visibility";
export interface AnimationDef {
  id: string;
  /** part id */
  target: string;
  type: AnimationType;
  /** translate · rotate — 축 */
  axis?: "x" | "y" | "z";
  /** translate = 움직이는 거리(m) · rotate = 각도(°, 없으면 계속 돈다) */
  distance?: NumExpr;
  /** translate · path = m/s · rotate = °/s · visibility = 초당 깜빡임 */
  speed?: NumExpr;
  /** path — paths 의 id */
  path?: string;
  /** loop = 처음으로 돌아가 반복 · pingpong = 갔다가 돌아옴 · once = 한 번 */
  mode?: "loop" | "pingpong" | "once";
  /** 예전 표기(true = loop) */
  loop?: boolean;
  /** 이 식이 참(≠0)일 때만 진행 — 예: "$flow.busy"(처리 중일 때만 컨베이어 화물이 흐름) */
  when?: NumExpr;
}
export interface PathDef {
  id: string;
  type: "closed" | "open";
  points: Vec3[];
}
/**
 * 동작 순서(시퀀스) — 판단이 있는 동작을 데이터로: 고르기 → 이동 → 붙이기 → 대기 … 를 차례로, loop 면 처음부터 다시.
 *   target = part id, index = 반복 도형의 몇 번째(식) — 없으면 part 전체 · group: true 면 index 가 그 part 의 몇 번째 그룹(반복 부모마다 복제된 것)
 *     — 도형 하나 대신 그 그룹을 통째로(자식 · 손자까지) 움직인다. 예: 소터 작업자 몸통 묶음 돌리기
 *   move.to = 부모 기준 좌표(그 축), speed = 단위/초(rotate 축이면 °/초 — op "turn")
 */
/** 대상 고르기 칸(move · turn · moveTo · scale · material · attach · detach · show · hide 공통) */
export interface TargetPick { target: string; index?: NumExpr; group?: boolean }
export type StepDef =
  | { op: "wait"; seconds: NumExpr }
  /** avoid = 같은 part 의 다른 도형이 앞쪽 이 거리(m) 안에 있으면 멈춰 기다림(최대 4초) — 로봇끼리 간격 */
  | ({ op: "move"; axis: "x" | "y" | "z"; to: NumExpr; speed: NumExpr; avoid?: NumExpr; avoidAngle?: NumExpr; avoidMax?: NumExpr; ease?: "linear" | "inout" } & TargetPick)
  /** shortest = 각도를 ±180° 안으로 감아 짧은 쪽으로 돈다 */
  | ({ op: "turn"; axis: "x" | "y" | "z"; to: NumExpr; speed: NumExpr; shortest?: boolean; ease?: "linear" | "inout" } & TargetPick)
  /** 세 축을 한 번에 곧게(또는 arc 높이의 포물선으로) — duration(초) 또는 speed(m/s) · ease "inout" = 부드럽게 출발 · 정지 */
  | ({ op: "moveTo"; to: Vec3; duration?: NumExpr; speed?: NumExpr; arc?: NumExpr; arcShape?: "parabola" | "sin"; ease?: "linear" | "inout"; avoid?: NumExpr; avoidAngle?: NumExpr; avoidMax?: NumExpr } & TargetPick)
  /** 크기 배율(하나면 세 축 같이 · [x,y,z]) — duration(초) 또는 speed(배율/초). 예: 박스가 줄어들며 사라짐 */
  | ({ op: "scale"; to: NumExpr | Vec3; duration?: NumExpr; speed?: NumExpr; ease?: "linear" | "inout" } & TargetPick)
  /** 재질 바꾸기 — materials 의 키. 예: 표시등 꺼짐 → 빨강 → 초록 */
  | ({ op: "material"; material: string } & TargetPick)
  /** 여러 단계를 동시에 — 모두 끝나면 다음으로(크레인 주행 + 승강 · 로봇 팔 관절) */
  | { op: "parallel"; steps: StepDef[] }
  /** 조건 — cond ≠ 0 이면 then, 아니면 else */
  | { op: "if"; cond: NumExpr; then: StepDef[]; else?: StepDef[] }
  /** times 번 반복 — var 를 주면 0 부터 번호를 넣는다 */
  | { op: "repeat"; times: NumExpr; var?: string; steps: StepDef[] }
  /** 흐름이 처리 중($flow.busy)이 될 때까지 기다린다(연결이 없으면 바로 지나감) */
  | { op: "busy" }
  /** cond 가 참이 될 때까지 기다림(max 초가 지나면 그냥 지나감) */
  | { op: "waitUntil"; cond: NumExpr; max?: NumExpr }
  /** seconds 동안 매 프레임 steps 를 한 번에(순간) 실행 — var 에 진행률 0~1. 매 프레임 계산이 필요한 동작(로봇 팔 IK 를 프레임마다 · 진행률을 따라가는 높이) */
  | { op: "during"; seconds: NumExpr; var?: string; steps: StepDef[] }
  /** 다른 동작 순서(id)를 하나 더 동시에 시작(코드의 rt.spawn) */
  | { op: "start"; sequence: string; with?: Record<string, NumExpr> }
  | { op: "pick"; var: string; count: NumExpr; skip?: NumExpr }
  | { op: "set"; var: string; value: NumExpr }
  | ({ op: "attach"; to: string; toIndex?: NumExpr } & TargetPick)
  | ({ op: "detach" } & TargetPick)
  | ({ op: "show" | "hide" } & TargetPick)
  /** 흐름 시뮬과 연결돼 있으면 일감 1건이 올 때까지 기다린다(연결이 없으면 바로 지나감) */
  | { op: "cycle" };
/**
 * for = 틀 — count 개를 만들어 같이 돌린다(로봇 번호마다 같은 순서). var 에 0 부터 번호.
 * local = 이 순서(틀이면 하나하나) 안에서만 쓰는 변수 이름 — 다른 순서와 섞이지 않는다(for.var 는 늘 local).
 * 변수 이름에 [식] 을 붙이면 번호 붙은 변수: set var "c[$vars.q]" → c_3 · 식에서 $vars.c[$vars.q]
 */
export interface SequenceDef { id: string; loop?: boolean; steps: StepDef[]; for?: { var: string; count: NumExpr }; local?: string[];
  /** false = 놓을 때 저절로 돌지 않음 — start 단계로만 시작(일감마다 하나씩 띄우는 순서) */
  auto?: boolean }
/** 파생 값 — 파라미터에서 계산한 중간 값. 식 또는 선택 값 → 숫자 표(lookup). 순서대로 계산, 뒤의 것은 앞의 것을 쓸 수 있다($vars.이름) */
export type DerivedDef = { name: string; expr: NumExpr } | { name: string; lookup: string; map: Record<string, number>; default?: number };
/** 앞으로: 상태 흐름(IDLE → REQUEST → MOVING …). 지금은 저장만 하고 렌더러는 쓰지 않는다 */
export interface StateMachineDef { initial?: string; states?: Array<{ id: string; animations?: string[]; next?: string; after?: NumExpr }> }
export interface MotionDef {
  animations?: AnimationDef[];
  paths?: PathDef[];
  sequences?: SequenceDef[];
  states?: StateMachineDef;
}

export interface MaterialDef {
  type?: "standard";
  color?: string;
  metalness?: number;
  roughness?: number;
  opacity?: number;
  /** 스스로 빛남(#RRGGBB) · 세기(0~10, 기본 1) — 표시등 · 화면 */
  emissive?: string;
  emissiveIntensity?: number;
  /**
   * 무늬(pattern.ts) — 바탕 = color.
   *   줄무늬 { type: "stripes", color2: "#22262c", scale: 3.2(1m 당 짙은 줄 수), angle: 45(아무 각도), fit: "meters" | "face" }
   *     기본 = 코드판 hazardTex(노랑 · 짙은 사선) · meters = 줄 간격이 도형 크기와 상관없이 m 기준 · face = 코드 M.hazard 처럼 면마다 한 번 늘여 붙임(scale = 면 하나에 줄 수, 기본 2)
   *   골판지 { type: "carton", color2?(테이프 띠) } = 코드 M.carton · 라벨 { type: "label", angle? } = 코드 M.label — 둘 다 면마다 한 번
   */
  pattern?: PatternDef;
}

/** param_schema 한 칸 — v1 과 같은 모양(key · label · type · default · min · max · step · options) */
export interface ParamSchemaEntry {
  key: string;
  label?: string;
  type: "int" | "float" | "select" | "bool";
  default: number | string | boolean;
  min?: number;
  max?: number;
  step?: number;
  options?: Array<{ value: string; label?: string }>;
  unit?: string;
  /** v1 표시용(모양 · 동작) — v2 에서는 bindings 가 정본 */
  affects?: string;
  /**
   * [N9] 옛 키 — 저장본(배치 값)에 이 칸 대신 옛 이름으로 들어 있으면 그 값을 읽는다(코드판 이름으로 바꾼 칸 · 공통 칸에서 옮긴 칸).
   *   옛 값이 이 칸 범위(min~max · 선택지 · true/false) 밖이면 버리고 기본값(뜻이 다른 「자동 = 0」 같은 값을 옮기지 않게)
   */
  aliases?: string[];
  /** [N9] 다른 칸 값이 이 중 하나일 때만 속성 칸에 보임(코드판 showIf — 예: 도크 종류 = 입고일 때만 「시간당 입고 수량」) */
  show_if?: { key: string; values: string[] };
}

/** 트윈 배치 · 흐름에 필요한 최소 값(없으면 기본) — 생김새와는 무관 */
export interface TwinMetaDef {
  category?: "보관" | "이송·분류" | "로봇·피킹" | "포장" | "건물";
  units?: Array<"pallet" | "case" | "tote" | "piece">;
  /** 시간당 처리 수 — 숫자 또는 파라미터 식("3600 / $params.cycle") */
  capacity_per_hour?: number | string;
  /**
   * [N6] 같은 종류 — 코드판 종류 id(예: "rack" · "rack_light" · "pick_station" · "vlm"). 코드가 설비 id 로 붙이는 공통 칸
   * (재고 · 보충 · 입구 버퍼 숨김 — specs/index applyCommonParams · isReplenSpec)과 단독 랙 규칙(rackAdapter)을 이 모델에도 적용한다.
   * 없으면 metadata.source "translated:<id>" 의 id
   */
  kind?: string;
  /**
   * [N6] 트윈 캔버스에서 레이아웃 탭 CAD 랙 렌더러로 그림 — "rack"(팔레트랙) · "rack_light"(경량랙 — 플로우랙 · 모빌랙만, 선반은 정의가 그림).
   * 파라미터 키는 코드판과 같아야 한다(bayCount · levelCount · slotCount · form · access). 정의의 랙 모양은 preview_only 로(코드판 previewOnly 와 같음)
   */
  cad?: "rack" | "rack_light";
  /**
   * [N9] 처리 중 화물을 정의가 직접 그린다($flow.progress 로 — 포장기 날개 접기 · 테이핑처럼). true 면 kind 의 코드판 화물 경로(flow)를
   * 물려받을 때 own 을 지킨다 → 흐름 층이 처리 중 화물을 숨김(두 번 그리지 않음). 없으면 own 을 뺀다(N8 기본)
   */
  flow_own?: boolean;
}

export interface TwinDefinitionV2 {
  format: typeof TWIN3D_V2;
  /** 기본 = json_model(이 JSON 으로 그린다). 기존 트윈 종류 id 를 쓰면 geometry 없이 그 종류로 그린다(v1 과 같음) */
  spec_id?: string;
  spec_name: string;
  model?: { id?: string; name?: string | null; model_no?: string | null; manufacturer?: string | null };
  params: Record<string, number | string | boolean>;
  param_schema: ParamSchemaEntry[];
  derived?: DerivedDef[];
  geometry?: GeometryDef;
  bindings?: BindingDef[];
  motion?: MotionDef;
  materials?: Record<string, MaterialDef>;
  metadata?: Record<string, unknown> & { twin?: TwinMetaDef };
}

/** DB(crm_catalog_models.twin_definition)에 넣는 부분 — 모델 정보 · format 은 빼고 형상에 필요한 것만 */
export type StoredDefinition = Pick<TwinDefinitionV2, "spec_name" | "param_schema" | "derived" | "geometry" | "bindings" | "motion" | "materials" | "metadata">;

export interface Issue { path: string; message: string }
